import {DetailsFieldsCard, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Trans, useTranslation} from 'react-i18next'

import {AlertCircleIcon, QueueListIcon} from '@/components/ui/icon/Icon'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {HttpGetAccessesDetailResponse} from '@/features/accesses/http/accesses.http'
import {useAccessesCalculations} from '../../hooks/useAccessesCalculations'
import {useMemo} from 'react'
import {Divider} from '@/components/ui/divider/Divider'
import {HttpGetPlannedAccessesDetailResponse} from '@/features/planned-accesses/http/plannedAccesses.http'
import {EventTypeKeyName} from '@/features/agenda/types'
import {useTheme} from 'styled-components'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {ButtonNavLink} from '@/components/ui/button-nav-link/ButtonNavLink'
import {routes} from '@/utilities/constants/routes'
import {useOutletContext} from 'react-router-dom'
import {ProjectExtended} from '../../types'
import {usePlannedAccessesCalculations} from '../../hooks/usePlannedAccessesCalculations'
import {calculateTotalAccesses} from '../../utils'
import {usePermissions} from '@/features/auth/store/store'

const findPlannedTreatments = (
    accessType: EventTypeKeyName,
    plannedAccesses?: HttpGetPlannedAccessesDetailResponse
) => {
    return plannedAccesses?.treatments.find(plannedAccess => plannedAccess.accessType === accessType)
}
const findPlannedInPersonAccesses = (
    accessType: EventTypeKeyName,
    plannedAccesses?: HttpGetPlannedAccessesDetailResponse
) => {
    return plannedAccesses?.in_person_accesses.find(access => access.accessType === accessType)
}
const findPlannedNonInPersonAccesses = (
    accessType: EventTypeKeyName,
    plannedAccesses?: HttpGetPlannedAccessesDetailResponse
) => {
    return plannedAccesses?.non_in_person_accesses.find(access => access.accessType === accessType)
}

export const AccessesCard = ({
    accesses,
    plannedAccesses
}: {
    accesses: HttpGetAccessesDetailResponse
    plannedAccesses?: HttpGetPlannedAccessesDetailResponse
}) => {
    const {t} = useTranslation()
    const {project} = useOutletContext<{project: ProjectExtended}>()
    const {
        totalAccesses,
        totalAccessesInPresence,
        totalTreatments,
        totalSingleAccesses,
        totalAccessInAbsence,
        totalUnaccountedAccesses
    } = useAccessesCalculations(accesses)

    const permissions = usePermissions()

    const {totalPlannedAccessesInPresence} = usePlannedAccessesCalculations(project.durationDays ?? 0, plannedAccesses)

    const totalAccessInAbsencePercentage = useMemo(() => {
        return project.durationDays
            ? Number(((totalAccessInAbsence / totalPlannedAccessesInPresence) * 100).toFixed(2))
            : 0
    }, [totalAccessInAbsence, totalPlannedAccessesInPresence, project.durationDays])

    const {palette} = useTheme()

    const accessesUrl = useMemo(() => {
        if (!project.code) {
            return routes.ACCESSES_ROUTE.handle.path
        }

        const filters = JSON.stringify({
            operator: 'and',
            fields: [
                {
                    key: 'projectCode',
                    operator: '=',
                    value: Number(project.code)
                }
            ]
        })

        return `${routes.ACCESSES_TABLE.handle.path}?filters=${encodeURIComponent(filters)}`
    }, [project.code])

    const getAlertTooltip = (numberOfAccesses: number, plannedAccesses?: number) => {
        if (!plannedAccesses || numberOfAccesses > plannedAccesses) {
            return (
                <Tooltip content={t('projects:over_planned')} variant="light" isOpenOnClickEnabled={false}>
                    <Flexbox align="center" justify="center">
                        <AlertCircleIcon color={palette.orange[600]} size={16} />
                    </Flexbox>
                </Tooltip>
            )
        } else if (numberOfAccesses < plannedAccesses) {
            return (
                <Tooltip content={t('projects:under_planned')} variant="light" isOpenOnClickEnabled={false}>
                    <Flexbox align="center" justify="center">
                        <AlertCircleIcon color={palette.orange[600]} size={16} />
                    </Flexbox>
                </Tooltip>
            )
        }
    }

    return (
        <DetailsFieldsSection
            variant="neutral"
            infoTitleSlot={
                <Text size="textLg">
                    <Trans i18nKey="projects:x_total_accesses" values={{value: totalAccesses}} />
                </Text>
            }
            title={t('accesses:completed')}
            width="100%"
            height="100%"
            headingEndSlot={
                permissions?.accesses.read && (
                    <ButtonNavLink size="sm" variant="secondary" to={accessesUrl}>
                        <QueueListIcon />
                        {t('projects:access_absence_registry')}
                    </ButtonNavLink>
                )
            }
        >
            <Flexbox direction="column" gap={4} fullWidth>
                <Text palette="neutral" shade="500" weight="500" size="textSm">
                    <Trans i18nKey="projects:patient_presence_count" values={{value: totalAccessesInPresence}} />
                </Text>
                <Flexbox direction="column" gap={1} fullWidth>
                    <Grid columns={3} fullWidth>
                        <GridItem spanColumn={2}>
                            <Text palette="neutral" shade="950" weight="500">
                                {t('projects:treatments')}
                            </Text>
                        </GridItem>
                        <GridItem>
                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                {totalTreatments}
                            </Text>
                        </GridItem>
                    </Grid>
                    <DetailsFieldsCard size="sm" fullWidth gap={2} direction="column">
                        {accesses.treatments.length > 0 ? (
                            accesses.treatments.map((treatment, index) => (
                                <Grid columns={3} gap={1} fullWidth key={index}>
                                    <GridItem spanColumn={2}>
                                        <Text palette="neutral" shade="950" weight="500" size="textSm">
                                            {treatment.accessName}
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Grid columns={2} columnGap={2}>
                                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                                {treatment.numberOfAccesses || '-'}
                                            </Text>
                                            {getAlertTooltip(
                                                treatment.numberOfAccesses,
                                                findPlannedTreatments(treatment.accessType, plannedAccesses)
                                                    ? calculateTotalAccesses(
                                                          project.durationDays ?? 0,
                                                          findPlannedTreatments(treatment.accessType, plannedAccesses)
                                                              ?.frequency ?? 'weekly',
                                                          findPlannedTreatments(treatment.accessType, plannedAccesses)
                                                              ?.numberOfAccesses ?? 0
                                                      )
                                                    : 0
                                            )}
                                        </Grid>
                                    </GridItem>
                                </Grid>
                            ))
                        ) : (
                            <Grid columns={3} gap={1} fullWidth>
                                <GridItem spanColumn={2}>
                                    <Text palette="neutral" shade="950" weight="500" size="textSm">
                                        -
                                    </Text>
                                </GridItem>

                                <Text palette="neutral" shade="950" weight="500" size="textSm">
                                    -
                                </Text>
                            </Grid>
                        )}
                    </DetailsFieldsCard>
                </Flexbox>

                <Flexbox direction="column" gap={1} fullWidth>
                    <Grid columns={3} fullWidth>
                        <GridItem spanColumn={2}>
                            <Text palette="neutral" shade="950" weight="500">
                                {t('commons:single_accesses')}
                            </Text>
                        </GridItem>
                        <GridItem>
                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                {totalSingleAccesses}
                            </Text>
                        </GridItem>
                    </Grid>
                    <DetailsFieldsCard size="sm" fullWidth gap={2} direction="column">
                        {accesses.in_person_accesses.length > 0 ? (
                            accesses.in_person_accesses.map((access, index) => (
                                <Grid columns={3} gap={1} fullWidth key={index}>
                                    <GridItem spanColumn={2}>
                                        <Text palette="neutral" shade="950" weight="500" size="textSm">
                                            {access.accessName}
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Grid columns={2} columnGap={2}>
                                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                                {access.numberOfAccesses || '-'}
                                            </Text>
                                            {getAlertTooltip(
                                                access.numberOfAccesses,
                                                findPlannedInPersonAccesses(access.accessType, plannedAccesses)
                                                    ?.numberOfAccesses
                                            )}
                                        </Grid>
                                    </GridItem>
                                </Grid>
                            ))
                        ) : (
                            <Grid columns={3} gap={1} fullWidth>
                                <GridItem spanColumn={2}>
                                    <Text palette="neutral" shade="950" weight="500" size="textSm">
                                        -
                                    </Text>
                                </GridItem>
                                <GridItem>
                                    <Text palette="neutral" shade="950" weight="500" size="textSm">
                                        -
                                    </Text>
                                </GridItem>
                            </Grid>
                        )}
                    </DetailsFieldsCard>
                </Flexbox>
                <Text palette="neutral" shade="500" weight="500" size="textSm">
                    <Trans
                        i18nKey="projects:patient_absence_count"
                        values={{total: totalAccessInAbsence, percentage: totalAccessInAbsencePercentage}}
                    />
                </Text>
                <Flexbox direction="column" gap={1} fullWidth>
                    <Grid columns={3} fullWidth>
                        <GridItem spanColumn={2}>
                            <Text palette="neutral" shade="950" weight="500">
                                {t('projects:absences_accesses')}
                            </Text>
                        </GridItem>
                        <GridItem>
                            <Text palette="neutral" shade="500" weight="500" size="textSm">
                                {totalAccessInAbsence}
                            </Text>
                        </GridItem>
                    </Grid>
                    <DetailsFieldsCard size="sm" fullWidth gap={2} direction="column">
                        {accesses.non_in_person_accesses.length > 0 ? (
                            accesses.non_in_person_accesses.map((access, index) => (
                                <Grid columns={3} gap={1} fullWidth key={index}>
                                    <GridItem spanColumn={2}>
                                        <Text palette="neutral" shade="950" weight="500" size="textSm">
                                            {access.accessName}
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Grid columns={2} columnGap={2}>
                                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                                {access.numberOfAccesses || '-'}
                                            </Text>
                                            {getAlertTooltip(
                                                access.numberOfAccesses,
                                                findPlannedNonInPersonAccesses(access.accessType, plannedAccesses)
                                                    ?.numberOfAccesses
                                            )}
                                        </Grid>
                                    </GridItem>
                                </Grid>
                            ))
                        ) : (
                            <Grid columns={3} gap={1} fullWidth>
                                <GridItem spanColumn={2}>
                                    <Text palette="neutral" shade="950" weight="500" size="textSm">
                                        -
                                    </Text>
                                </GridItem>
                                <GridItem>
                                    <Text palette="neutral" shade="950" weight="500" size="textSm">
                                        -
                                    </Text>
                                </GridItem>
                            </Grid>
                        )}
                    </DetailsFieldsCard>
                </Flexbox>
                {totalUnaccountedAccesses > 0 && (
                    <>
                        <Divider direction="horizontal" />
                        <Flexbox direction="column" gap={1} fullWidth>
                            <Grid columns={3} fullWidth>
                                <GridItem spanColumn={2}>
                                    <Text palette="neutral" shade="950" weight="500">
                                        {t('projects:unaccounted_accesses')}
                                    </Text>
                                </GridItem>
                                <GridItem>
                                    <Text palette="neutral" shade="500" weight="500" size="textSm">
                                        {totalUnaccountedAccesses}
                                    </Text>
                                </GridItem>
                            </Grid>
                            <DetailsFieldsCard size="sm" fullWidth gap={2} direction="column">
                                {accesses.unaccounted_accesses.map((access, index) => (
                                    <Grid columns={3} gap={1} fullWidth key={index}>
                                        <GridItem spanColumn={2}>
                                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                                {access.accessName}
                                            </Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                                {access.numberOfAccesses}
                                            </Text>
                                        </GridItem>
                                    </Grid>
                                ))}
                            </DetailsFieldsCard>
                        </Flexbox>
                    </>
                )}
            </Flexbox>
        </DetailsFieldsSection>
    )
}
